import React, { createContext, useContext, useState, useRef, useCallback } from 'react';
import api from './api/axiosConfig';

const BigBoardTableContext = createContext();

const STALE_DURATION_MS = 2 * 60 * 1000; // 2 minutes

export const BigBoardTableProvider = ({ children }) => {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Sélections globales
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);

  // Stocker la position de scroll pour la restaurer
  const [scrollPosition, setScrollPosition] = useState(0);

  // Orientation de l'affichage (vertical = colonnes, horizontal = lignes)
  const [orientation, setOrientationState] = useState(() => {
    const stored = localStorage.getItem('bigBoardOrientation');
    return stored === 'horizontal' ? 'horizontal' : 'vertical';
  });
  const setOrientation = useCallback((value) => {
    if (value !== 'vertical' && value !== 'horizontal') return;
    setOrientationState(value);
    localStorage.setItem('bigBoardOrientation', value);
  }, []);

  const lastFetchTime = useRef(null);

  const refreshData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/bigboard');
      setData(response.data);
      setError(null);
      lastFetchTime.current = Date.now();
    } catch (error) {
      console.error('Error al recuperar los Big Boards:', error);
      setError('No se pueden cargar los Big Boards.');
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshDataIfStale = useCallback(() => {
    const isStale =
      lastFetchTime.current === null ||
      Date.now() - lastFetchTime.current > STALE_DURATION_MS;
    if (isStale) refreshData();
  }, [refreshData]);

  const value = {
    expandedRows,
    setExpandedRows,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    data,
    setData,
    loading,
    setLoading,
    error,
    setError,
    selectedPlayer,
    setSelectedPlayer,
    selectedUser,
    setSelectedUser,
    tags,
    setTags,
    selectedTags,
    setSelectedTags,
    refreshData,
    refreshDataIfStale,
    scrollPosition,
    setScrollPosition,
    orientation,
    setOrientation
  };

  return (
    <BigBoardTableContext.Provider value={value}>
      {children}
    </BigBoardTableContext.Provider>
  );
};

export const useBigBoardTable = () => {
  const context = useContext(BigBoardTableContext);
  if (context === undefined) {
    throw new Error(
      'useBigBoardTable must be used within a BigBoardTableProvider'
    );
  }
  return context;
};
